import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Switch,
  FormControlLabel,
  Paper,
  Typography,
  CircularProgress,
  IconButton,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Alert,
  InputAdornment,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon, ArrowBack as ArrowBackIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { Modal } from 'antd';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { PackageService } from '../../../api/packageService';
import { PackageCreateRequest, PackageUpdateRequest, PackageMenuResponse } from '../../../@type/package.types';
import CancelButton from '../../../components/common/buttons/CancelButton';
import LabeledInput from '../../../components/common/fields/LabeledInput';
import SelectField from '../../../components/common/fields/SelectField';
import InheritsPackageSelect from './InheritsPackageSelect';
import MenuAssignment from './MenuAssignment';
import PackagePriceManagement from './PackagePriceManagement';
import axiosInstance from '../../../axios/axios-instance';
import { formatNumber } from '../../../utils/utils';
import { useBreadcrumbLabel } from "../../../components/breadcrumb/BreadcrumbContext";

interface Country {
  id: number;
  name: string;
  code: string;
  currency: string;
  currency_symbol?: string;
}

const PackageForm: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [assignedMenus, setAssignedMenus] = useState<PackageMenuResponse | null>(null);
  const [selectedMenuIds, setSelectedMenuIds] = useState<number[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [countryPrices, setCountryPrices] = useState<{ [countryId: number]: number }>({});
  const [displayPrices, setDisplayPrices] = useState<{ [countryId: number]: string }>({});
  const [displayGlobalPrice, setDisplayGlobalPrice] = useState<string>('0');
  const [formData, setFormData] = useState<PackageCreateRequest>({
    code: '',
    name: '',
    description: '',
    price: 0,
    billing_period: 'monthly',
    max_staff: 5,
    is_active: true,
    ai_chat_enabled: false,
    pricing_strategy: 'global',
    default_currency: 'USD',
    inherits_package_id: null,
    menu_item_ids: [],
  });

  useBreadcrumbLabel(isEdit ? formData.name : undefined, 1);

  useEffect(() => {
    if (isEdit && id) {
      loadPackage(Number(id));
    }
    // Fetch countries for per-country pricing
    fetchCountries();
  }, [id, isEdit]);

  // Format number for display (with thousand separators)
  const formatDisplayNumber = (value: string): string => {
    // Remove all non-digit characters except decimal point
    const cleaned = value.replace(/[^\d.]/g, '');

    // Split by decimal point
    const parts = cleaned.split('.');

    // Format integer part with thousand separators
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    // Keep only 2 decimal places
    if (parts.length > 1) {
      const decimalPart = parts[1].substring(0, 2);
      return `${integerPart}.${decimalPart}`;
    }

    return integerPart;
  };

  // Parse display number to raw number
  const parseDisplayNumber = (value: string): number => {
    const cleaned = value.replace(/[^\d.]/g, '');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  };

  const fetchCountries = async () => {
    // Check localStorage cache first
    const cachedCountries = localStorage.getItem('countries');
    if (cachedCountries) {
      try {
        const countriesData = JSON.parse(cachedCountries);
        console.log('Countries loaded from cache:', countriesData);
        setCountries(countriesData);
        return;
      } catch (error) {
        console.error('Error parsing cached countries:', error);
        // Continue to fetch from API if cache is invalid
      }
    }

    // Fetch from API if no cache
    setLoadingCountries(true);
    try {
      const response = await axiosInstance.get('/admin/common/countries');
      console.log('Countries API Response:', response);
      // Access data from response.data according to admin API response structure
      const countriesData = response.data.data || response.data || [];
      console.log('Countries to set:', countriesData);
      setCountries(countriesData);
      // Cache the result
      localStorage.setItem('countries', JSON.stringify(countriesData));
    } catch (error) {
      console.error('Error fetching countries:', error);
      toast.error(t('packages.messages.fetch_countries_error') || 'Failed to load countries');
    } finally {
      setLoadingCountries(false);
    }
  };

  const loadPackage = async (packageId: number) => {
    setLoading(true);
    try {
      const response = await PackageService.getPackageById(packageId);
      console.log('Package API response:', response);

      // Server returns: { success: true, code: "OK", data: {...} }
      // Extract actual package data from response.data.data
      const packageData = response.data.data;
      console.log('Package data:', packageData);

      // Map to form data - server returns snake_case
      const price = packageData.price || 0;
      setFormData({
        code: packageData.code || '',
        name: packageData.name || '',
        description: packageData.description || '',
        price: price,
        billing_period: packageData.billing_period || 'monthly',
        max_staff: packageData.max_staff || 5,
        is_active: packageData.is_active !== undefined ? packageData.is_active : true,
        ai_chat_enabled: packageData.ai_chat_enabled !== undefined ? packageData.ai_chat_enabled : false,
        pricing_strategy: packageData.pricing_strategy || 'global',
        default_currency: packageData.default_currency || 'USD',
        inherits_package_id: packageData.inherits_package_id ?? null,
      });

      // Initialize display price
      if (price > 0) {
        setDisplayGlobalPrice(formatNumber(price, 2));
      }

      // Extract assigned menus if available
      if (packageData.assigned_menus) {
        setAssignedMenus({
          package: {
            id: packageData.id,
            code: packageData.code,
            name: packageData.name,
          },
          package_id: packageData.id,
          package_code: packageData.code,
          package_name: packageData.name,
          assigned_menus: packageData.assigned_menus,
          menu_items: packageData.assigned_menus,
        });
      }
    } catch (error) {
      console.error('Failed to load package:', error);
      toast.error('Failed to load package details');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEdit && id) {
        await PackageService.updatePackage(Number(id), formData as PackageUpdateRequest);
        toast.success(t('packages.updateSuccess'));
      } else {
        // For create, include selected menu IDs
        const createData: any = {
          ...formData,
          menu_item_ids: selectedMenuIds,
        };

        // If per_country pricing, include country_prices
        if (formData.pricing_strategy === 'per_country') {
          const countryPricesArray = Object.entries(countryPrices)
            .filter(([_, price]) => price > 0)
            .map(([countryId, price]) => ({
              country_id: parseInt(countryId),
              price: price,
            }));
          createData.country_prices = countryPricesArray;
        }

        await PackageService.createPackage(createData);
        toast.success(t('packages.createSuccess'));
      }
      // Wait a bit for notification to show before navigating
      setTimeout(() => {
        navigate('/settings/packages');
      }, 500);
    } catch (error) {
      console.error('Failed to save package:', error);
      toast.error(isEdit ? t('packages.updateError') : t('packages.createError'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof PackageCreateRequest) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const target = e.target as HTMLInputElement;
    let value: any = target.value;

    // Handle checkbox
    if (target.type === 'checkbox') {
      value = target.checked;
    }
    // Handle number fields
    else if (target.type === 'number') {
      value = target.value === '' ? 0 : Number(target.value);
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePricingStrategyChange = (newStrategy: 'global' | 'per_country') => {
    if (isEdit && id) {
      // In edit mode, show confirmation dialog
      Modal.confirm({
        title: t('packages.changePricingStrategyTitle'),
        content: t('packages.changePricingStrategyWarning'),
        okText: t('btn.confirm'),
        cancelText: t('btn.cancel'),
        okButtonProps: { style: { background: '#ba2156' } },
        onOk: async () => {
          try {
            await PackageService.updatePricingStrategy(Number(id), newStrategy, true);
            toast.success(t('packages.pricingStrategyUpdated'));
            setFormData((prev) => ({ ...prev, pricing_strategy: newStrategy }));
            // Reload package data to get updated prices
            loadPackage(Number(id));
          } catch (error) {
            console.error('Failed to update pricing strategy:', error);
            toast.error(t('packages.pricingStrategyUpdateError'));
          }
        },
      });
    } else {
      // In create mode, just update the state
      setFormData((prev) => ({ ...prev, pricing_strategy: newStrategy }));
    }
  };

  if (loading && isEdit) {
    return (
      <div className="flex justify-center items-center h-64">
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <div className="p-6">
        <div className="mb-6 flex items-center gap-3">
          <IconButton
            onClick={() => navigate('/settings/packages')}
            size="small"
            sx={{ '&:hover': { bgcolor: 'action.hover' } }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" className="font-bold">
            {isEdit ? t('packages.edit') : t('packages.create')}
          </Typography>
        </div>

        <Paper className="p-6 max-w-2xl mx-auto">
        <form onSubmit={handleSubmit} className="space-y-6">
          <LabeledInput
            fullWidth
            label={t('packages.code')}
            value={formData.code}
            onChange={handleChange('code')}
            required
            inputProps={{ required: true }}
            disabled={isEdit}
            helperText="Unique package code (lowercase, alphanumeric + underscore)"
          />

          <LabeledInput
            fullWidth
            label={t('packages.name')}
            value={formData.name}
            onChange={handleChange('name')}
            required
            inputProps={{ required: true }}
          />

          <LabeledInput
            fullWidth
            label={t('packages.description')}
            value={formData.description}
            onChange={handleChange('description')}
            multiline
            rows={3}
          />

          <Box>
            <Box component="label" sx={{ display: 'block', mb: 0.5, fontSize: '14px' }}>
              {t('packages.billingPeriod')}
              <span style={{ color: '#ff4d4f' }}> *</span>
            </Box>
            <SelectField
              value={formData.billing_period}
              searchable={false}
              matchAnchorWidth
              options={[
                { value: 'monthly', label: t('packages.monthly') },
                { value: 'yearly', label: t('packages.yearly') },
              ]}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, billing_period: String(value ?? prev.billing_period) as typeof prev.billing_period }))
              }
            />
          </Box>

          <InheritsPackageSelect
            value={formData.inherits_package_id ?? null}
            excludeId={isEdit ? Number(id) : undefined}
            onChange={(value) => setFormData((prev) => ({ ...prev, inherits_package_id: value }))}
          />

          <Divider className="my-6" />

          {/* Pricing Strategy Section */}
          <Box className="mb-6">
            <FormControl component="fieldset">
              <FormLabel component="legend" className="font-semibold mb-2">
                {t('packages.pricingStrategy')}
              </FormLabel>
              <RadioGroup
                value={formData.pricing_strategy}
                onChange={(e) => handlePricingStrategyChange(e.target.value as 'global' | 'per_country')}
                row
              >
                <FormControlLabel
                  value="global"
                  control={<Radio color="primary" />}
                  label={t('packages.globalPricing')}
                />
                <FormControlLabel
                  value="per_country"
                  control={<Radio color="primary" />}
                  label={t('packages.perCountryPricing')}
                />
              </RadioGroup>
            </FormControl>

            {/* Global Pricing Fields */}
            {formData.pricing_strategy === 'global' && (
              <Box mt={3}>
                <div className="grid grid-cols-2 gap-4">
                  <LabeledInput
                    fullWidth
                    label={t('packages.currency')}
                    value="USD"
                    disabled
                    helperText={t('packages.currencyHelperText')}
                  />
                  <LabeledInput
                    fullWidth
                    type="text"
                    label={t('packages.price')}
                    value={displayGlobalPrice}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      // Only allow numbers, commas, and decimal point
                      if (inputValue === '' || /^[\d,]*\.?\d*$/.test(inputValue)) {
                        // Format for display
                        const formatted = formatDisplayNumber(inputValue);
                        setDisplayGlobalPrice(formatted);

                        // Parse and save raw number
                        const rawValue = parseDisplayNumber(formatted);
                        if (rawValue >= 0) {
                          setFormData((prev) => ({ ...prev, price: rawValue }));
                        }
                      }
                    }}
                    onBlur={() => {
                      // Format on blur to ensure proper formatting
                      if (formData.price > 0) {
                        setDisplayGlobalPrice(formatNumber(formData.price, 2));
                      }
                    }}
                    required
                    inputProps={{ required: true }}
                    startAdornment={<InputAdornment position="start">$</InputAdornment>}
                  />
                </div>
                <Alert severity="info" className="mt-3">
                  {t('packages.globalPricingInfo')}
                </Alert>
              </Box>
            )}

            {/* Per-Country Pricing */}
            {formData.pricing_strategy === 'per_country' && (
              <Box mt={3}>
                {isEdit && id ? (
                  <PackagePriceManagement
                    packageId={Number(id)}
                    globalPrice={formData.price}
                    globalCurrency={formData.default_currency || 'USD'}
                  />
                ) : (
                  <Box>
                    <Typography variant="subtitle2" className="mb-3">
                      {t('packages.perCountryPricing')}
                    </Typography>
                    <Paper variant="outlined" className="p-4" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {loadingCountries ? (
                        <Box className="flex justify-center p-4">
                          <CircularProgress size={24} />
                        </Box>
                      ) : countries.length === 0 ? (
                        <Alert severity="warning">
                          No countries available. Please contact administrator.
                        </Alert>
                      ) : (
                        <div className="space-y-3">
                          {countries.map((country) => (
                            <Box key={country.id} className="grid grid-cols-2 gap-4">
                              <LabeledInput
                                fullWidth
                                label="Country"
                                value={`${country.name} (${country.currency})`}
                                disabled
                              />
                              <LabeledInput
                                fullWidth
                                type="text"
                                label={`Price in ${country.currency}`}
                                value={displayPrices[country.id] || ''}
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  // Only allow numbers, commas, and decimal point
                                  if (inputValue === '' || /^[\d,]*\.?\d*$/.test(inputValue)) {
                                    // Format for display
                                    const formatted = formatDisplayNumber(inputValue);
                                    setDisplayPrices((prev) => ({
                                      ...prev,
                                      [country.id]: formatted,
                                    }));

                                    // Parse and save raw number
                                    const rawValue = parseDisplayNumber(formatted);
                                    if (rawValue >= 0) {
                                      setCountryPrices((prev) => ({
                                        ...prev,
                                        [country.id]: rawValue,
                                      }));
                                    }
                                  }
                                }}
                                onBlur={() => {
                                  // Format on blur to ensure proper formatting
                                  if (countryPrices[country.id] > 0) {
                                    setDisplayPrices((prev) => ({
                                      ...prev,
                                      [country.id]: formatNumber(countryPrices[country.id], 2),
                                    }));
                                  }
                                }}
                                startAdornment={<InputAdornment position="start">
                                      {country.currency_symbol || country.currency}
                                    </InputAdornment>}
                              />
                            </Box>
                          ))}
                        </div>
                      )}
                    </Paper>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          <Divider className="my-6" />

          <LabeledInput
            fullWidth
            type="number"
            label={t('packages.maxStaff')}
            value={formData.max_staff}
            onChange={handleChange('max_staff')}
            helperText="Maximum number of staff members allowed"
          />

          <div className="flex gap-6">
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={handleChange('is_active')}
                  color="primary"
                />
              }
              label={t('packages.active')}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.ai_chat_enabled || false}
                  onChange={handleChange('ai_chat_enabled')}
                  color="primary"
                />
              }
              label={t('packages.aiChatEnabled', 'AI Chat Support')}
            />

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Note:</strong> This package toggle is only the per-package feature gate.
                AI Chat is available only when the matching app global setting is also enabled.
              </Typography>
            </Alert>
          </div>

          <Divider className="my-6" />

          {/* Menu Assignment Section - Show in create mode */}
          {!isEdit && (
            <Box className="mb-6">
              <Typography variant="h6" className="font-semibold mb-3">
                {t('packages.menuAssignment')}
              </Typography>
              <MenuAssignment
                selectedMenuIds={selectedMenuIds}
                onChange={setSelectedMenuIds}
                disabled={loading}
              />
            </Box>
          )}

          {/* Assigned Menus Section - Only show in edit mode */}
          {isEdit && (
            <>
              <Divider className="my-6" />

              <Box className="mt-6">
                <div className="flex justify-between items-center mb-4">
                  <Typography variant="h6" className="font-semibold">
                    {t('packages.assignedMenus')}
                  </Typography>
                  <CancelButton
                    startIcon={<SettingsIcon />}
                    onClick={() => navigate(`/settings/packages/${id}/menus`)}
                  >
                    {t('packages.configureMenus')}
                  </CancelButton>
                </div>

                {assignedMenus && (assignedMenus.assigned_menus || assignedMenus.menu_items)?.length ? (
                  <Paper variant="outlined" className="p-4">
                    <List dense>
                      {(assignedMenus.assigned_menus || assignedMenus.menu_items || []).map((menu) => (
                        <ListItem key={menu.id}>
                          <ListItemText
                            primary={menu.name}
                            secondary={`Code: ${menu.code}`}
                          />
                          <Chip
                            label={menu.is_enabled ? 'Enabled' : 'Disabled'}
                            size="small"
                            color={menu.is_enabled ? 'success' : 'default'}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                ) : (
                  <Paper
                    variant="outlined"
                    className="p-4 text-center"
                    sx={{ color: 'var(--app-text-muted)' }}
                  >
                    <Typography variant="body2">
                      {t('packages.noMenusAssigned')}
                    </Typography>
                    <Box mt={2}>
                      <CancelButton onClick={() => navigate(`/settings/packages/${id}/menus`)}>
                        {t('packages.assignMenusNow')}
                      </CancelButton>
                    </Box>
                  </Paper>
                )}
              </Box>
            </>
          )}

          <div className="flex gap-4 justify-end pt-4">
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={() => navigate('/settings/packages')}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              disabled={loading}
              className="bg-buttton"
              sx={{ textTransform: 'capitalize' }}
            >
              {loading ? <CircularProgress size={24} /> : 'Save'}
            </Button>
          </div>
        </form>
      </Paper>
    </div>
    </>
  );
};

export default PackageForm;
